import type { DateValue } from "@internationalized/date";
import type { DateRange } from "reka-ui";
import type { Matcher } from "reka-ui/date";
import { CalendarDate } from "@internationalized/date";
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeader,
  CalendarNext,
  CalendarPrev,
  CalendarRoot,
} from "reka-ui";
import { computed, defineComponent, shallowRef, watch } from "vue";
import Icon from "@/components/ui/icon/Icon";
import { calendarDayCount } from "@/utils/calendar-date";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

function getMonthNames(locale: string): string[] {
  const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
  return Array.from({ length: 12 }, (_, i) => formatter.format(new Date(2024, i, 1)));
}

const YEAR_RANGE = 20;

const Root = defineComponent({
  name: "RangeCalendar",
  props: {
    modelValue: p.custom<DateRange | null>([Object]),
    placeholder: p.custom<DateValue | undefined>([Object]),
    locale: p.string("en-US"),
    weekStartsOn: p.number<0 | 1 | 2 | 3 | 4 | 5 | 6>(0),
    weekdayFormat: p.string<"narrow" | "short" | "long">("narrow"),
    fixedWeeks: p.boolean(false),
    disabled: p.boolean(false),
    readonly: p.boolean(false),
    isDateDisabled: p.func<Matcher>(),
    isDateUnavailable: p.func<Matcher>(),
    minValue: p.custom<DateValue | undefined>([Object]),
    maxValue: p.custom<DateValue | undefined>([Object]),
    maximumDays: p.number(),
    pagedNavigation: p.boolean(true),
  },
  emits: {
    "update:modelValue": (_date: DateRange) => true,
    "update:placeholder": (_date: DateValue) => true,
    "update:startValue": (_date: DateValue | undefined) => true,
  },
  setup(props, { emit, attrs }) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const years = Array.from(
      { length: YEAR_RANGE * 2 + 1 },
      (_, i) => currentYear - YEAR_RANGE + i
    );

    // Two independent placeholders for left/right month views
    const leftPlaceholder = shallowRef<DateValue>(
      props.placeholder ?? new CalendarDate(currentYear, currentMonth, 1)
    );
    const rightPlaceholder = shallowRef<DateValue>(
      new CalendarDate(currentYear, currentMonth + 1 > 12 ? 1 : currentMonth + 1, 1)
    );

    // Range selection state
    const rangeStart = shallowRef<DateValue | undefined>(undefined);
    const rangeEnd = shallowRef<DateValue | undefined>(undefined);
    const hoverDate = shallowRef<DateValue | undefined>(undefined);

    // Sync from external modelValue
    watch(
      () => props.modelValue,
      (val) => {
        if (val?.start && val?.end) {
          rangeStart.value = val.start;
          rangeEnd.value = val.end;
          leftPlaceholder.value = new CalendarDate(val.start.year, val.start.month, 1);
          // Show end month on right (or start+1 if same month)
          if (val.start.year === val.end.year && val.start.month === val.end.month) {
            const nextMonth = val.start.month + 1;
            rightPlaceholder.value = new CalendarDate(
              nextMonth > 12 ? val.start.year + 1 : val.start.year,
              nextMonth > 12 ? 1 : nextMonth,
              1
            );
          } else {
            rightPlaceholder.value = new CalendarDate(val.end.year, val.end.month, 1);
          }
        } else if (!val) {
          rangeStart.value = undefined;
          rangeEnd.value = undefined;
        }
      },
      { immediate: true }
    );

    function handleDateClick(date: DateValue) {
      if (props.disabled || props.readonly) return;

      if (!rangeStart.value || rangeEnd.value) {
        // Start new selection
        rangeStart.value = date;
        rangeEnd.value = undefined;
        hoverDate.value = undefined;
        emit("update:startValue", date);
      } else {
        // Complete selection
        let start = rangeStart.value;
        let end = date;
        if (end.compare(start) < 0) {
          [start, end] = [end, start];
        }
        // Enforce maximumDays constraint
        if (props.maximumDays && calendarDayCount(start, end) > props.maximumDays) {
          return;
        }
        rangeStart.value = start;
        rangeEnd.value = end;
        hoverDate.value = undefined;
        emit("update:modelValue", { start, end } as DateRange);
        // Auto-navigate right calendar to show end date
        rightPlaceholder.value = new CalendarDate(end.year, end.month, 1);
      }
    }

    // Compute effective disabled dates (includes maximumDays constraint during selection)
    const effectiveIsDateDisabled = computed<Matcher | undefined>(() => {
      const baseMatcher = props.isDateDisabled;
      const maxDays = props.maximumDays;
      const start = rangeStart.value;
      const hasEnd = rangeEnd.value;

      if (!maxDays || !start || hasEnd) return baseMatcher;

      return (date: DateValue) => {
        if (baseMatcher?.(date)) return true;
        return calendarDayCount(date, start) > maxDays;
      };
    });

    function isOutOfMaxRange(day: DateValue): boolean {
      const start = rangeStart.value;
      if (!start || rangeEnd.value || !props.maximumDays) return false;
      return calendarDayCount(day, start) > props.maximumDays;
    }

    function getCellClass(day: DateValue): string {
      if (isOutOfMaxRange(day)) {
        return "text-muted-foreground! opacity-50! pointer-events-none!";
      }

      const start = rangeStart.value;
      if (!start) return "";

      const end = rangeEnd.value;
      const hover = hoverDate.value;
      const previewEnd = end ?? hover;

      if (!previewEnd) {
        // Only start selected, no hover
        return day.compare(start) === 0 ? "bg-accent-9! text-accent-contrast!" : "";
      }

      // Determine effective start/end (handle hover before start)
      let effectiveStart = start;
      let effectiveEnd = previewEnd;
      if (previewEnd.compare(start) < 0) {
        effectiveStart = previewEnd;
        effectiveEnd = start;
      }

      const cmpStart = day.compare(effectiveStart);
      const cmpEnd = day.compare(effectiveEnd);

      if (cmpStart === 0 && cmpEnd === 0) {
        return "bg-accent-9! text-accent-contrast!";
      }
      if (cmpStart === 0) {
        return "bg-accent-9! text-accent-contrast! rounded-l-md! rounded-r-none!";
      }
      if (cmpEnd === 0) {
        return "bg-accent-9! text-accent-contrast! rounded-r-md! rounded-l-none!";
      }
      if (cmpStart > 0 && cmpEnd < 0) {
        return "bg-accent-3A! text-foreground! rounded-none!";
      }

      return "";
    }

    // Enforce left < right month constraint
    function enforceMonthOrder(changed: "left" | "right") {
      const left = leftPlaceholder.value;
      const right = rightPlaceholder.value;
      const leftVal = left.year * 12 + left.month;
      const rightVal = right.year * 12 + right.month;

      if (leftVal >= rightVal) {
        if (changed === "left") {
          const m = left.month + 1;
          rightPlaceholder.value = new CalendarDate(
            m > 12 ? left.year + 1 : left.year,
            m > 12 ? 1 : m,
            1
          );
        } else {
          const m = right.month - 1;
          leftPlaceholder.value = new CalendarDate(
            m < 1 ? right.year - 1 : right.year,
            m < 1 ? 12 : m,
            1
          );
        }
      }
    }

    function handleMonthSelect(month: number, side: "left" | "right") {
      const ref = side === "left" ? leftPlaceholder : rightPlaceholder;
      ref.value = new CalendarDate(ref.value.year, month, 1);
      enforceMonthOrder(side);
    }

    function handleYearSelect(year: number, side: "left" | "right") {
      const ref = side === "left" ? leftPlaceholder : rightPlaceholder;
      ref.value = new CalendarDate(year, ref.value.month, 1);
      enforceMonthOrder(side);
    }

    return () => {
      const monthNames = getMonthNames(props.locale);
      const selectClass =
        "text-sm font-medium capitalize appearance-none border-0 bg-transparent cursor-pointer outline-none focus:ring-0 p-0 pr-0 hover:text-accent-11 transition-colors";
      const selectStyle = { backgroundImage: "none" };

      function renderMonth(
        placeholder: DateValue,
        onPlaceholder: (d: DateValue) => void,
        side: "left" | "right"
      ) {
        return (
          <CalendarRoot
            key={`${side}-${rangeStart.value?.toString() ?? "none"}-${rangeEnd.value ? "end" : "sel"}`}
            class="p-3"
            locale={props.locale}
            weekStartsOn={props.weekStartsOn}
            weekdayFormat={props.weekdayFormat}
            fixedWeeks={props.fixedWeeks}
            numberOfMonths={1}
            disabled={props.disabled}
            readonly={props.readonly}
            isDateDisabled={effectiveIsDateDisabled.value}
            isDateUnavailable={props.isDateUnavailable}
            minValue={props.minValue}
            maxValue={props.maxValue}
            placeholder={placeholder}
            onUpdate:placeholder={(d: DateValue) => onPlaceholder(d)}
            onUpdate:modelValue={(d: DateValue | undefined) => {
              if (d) handleDateClick(d);
            }}
            v-slots={{
              default: ({
                grid,
                weekDays,
              }: {
                grid: { value: DateValue; rows: DateValue[][] }[];
                weekDays: string[];
              }) => {
                const month = grid[0];
                return (
                  <div class="flex flex-col gap-4">
                    <CalendarHeader class="relative flex w-full items-center justify-between gap-1">
                      <CalendarPrev class="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50">
                        <Icon icon="lucide:chevron-left" class="h-4 w-4" />
                      </CalendarPrev>

                      <div class="flex items-center gap-1">
                        <select
                          class={selectClass}
                          style={selectStyle}
                          value={month.value.month}
                          onChange={(e: Event) =>
                            handleMonthSelect(Number((e.target as HTMLSelectElement).value), side)
                          }
                        >
                          {monthNames.map((name, i) => (
                            <option key={i} value={i + 1}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <select
                          class={selectClass}
                          style={selectStyle}
                          value={month.value.year}
                          onChange={(e: Event) =>
                            handleYearSelect(Number((e.target as HTMLSelectElement).value), side)
                          }
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>

                      <CalendarNext class="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50">
                        <Icon icon="lucide:chevron-right" class="h-4 w-4" />
                      </CalendarNext>
                    </CalendarHeader>

                    <CalendarGrid class="w-full border-collapse">
                      <CalendarGridHead>
                        <CalendarGridRow class="flex">
                          {weekDays.map((day) => (
                            <CalendarHeadCell
                              key={day}
                              class="text-muted-foreground w-8 rounded-md text-center text-[0.8rem] font-normal"
                            >
                              {day}
                            </CalendarHeadCell>
                          ))}
                        </CalendarGridRow>
                      </CalendarGridHead>
                      <CalendarGridBody>
                        {month.rows.map((week, weekIdx) => (
                          <CalendarGridRow key={weekIdx} class="mt-2 flex w-full">
                            {week.map((day) => (
                              <CalendarCell
                                key={day.toString()}
                                date={day}
                                class="relative p-0 text-center text-sm"
                              >
                                <div
                                  onMouseenter={() => {
                                    if (rangeStart.value && !rangeEnd.value) {
                                      hoverDate.value = day;
                                    }
                                  }}
                                >
                                  <CalendarCellTrigger
                                    day={day}
                                    month={month.value}
                                    class={cn(
                                      "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm tabular-nums transition-colors",
                                      "focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-none",
                                      "hover:bg-muted hover:text-foreground",
                                      "data-today:bg-accent-3 data-today:text-accent-11 data-today:font-medium",
                                      "data-disabled:text-muted-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
                                      "data-outside-view:text-muted-foreground/50 data-outside-view:pointer-events-none",
                                      "data-unavailable:text-danger-9 data-unavailable:line-through",
                                      getCellClass(day)
                                    )}
                                  />
                                </div>
                              </CalendarCell>
                            ))}
                          </CalendarGridRow>
                        ))}
                      </CalendarGridBody>
                    </CalendarGrid>
                  </div>
                );
              },
            }}
          />
        );
      }

      return (
        <div class={cn("flex flex-col gap-0 px-0 sm:flex-row sm:gap-0", attrs.class as string)}>
          {renderMonth(
            leftPlaceholder.value,
            (d) => {
              leftPlaceholder.value = d;
              enforceMonthOrder("left");
            },
            "left"
          )}
          {renderMonth(
            rightPlaceholder.value,
            (d) => {
              rightPlaceholder.value = d;
              enforceMonthOrder("right");
            },
            "right"
          )}
        </div>
      );
    };
  },
});

export default {
  Root,
};
